import { create } from 'zustand';

export interface DashboardWidget {
  id: string;
  title: string;
  visible: boolean;
  order: number;
}

const DEFAULT_WIDGETS: DashboardWidget[] = [
  { id: 'stats', title: 'Key Metrics', visible: true, order: 0 },
  { id: 'today-strip', title: 'Today Stats', visible: true, order: 1 },
  { id: 'module-health', title: 'Module Health', visible: true, order: 2 },
  { id: 'action-chart', title: 'Action Usage Chart', visible: true, order: 3 },
  { id: 'action-breakdown', title: 'Action Breakdown', visible: true, order: 4 },
  { id: 'revenue-summary', title: 'Revenue by Status', visible: true, order: 5 },
  { id: 'channel-volume', title: 'Channel Volume', visible: true, order: 6 },
  { id: 'quick-actions', title: 'Quick Actions', visible: true, order: 7 },
  { id: 'activity', title: 'Recent Activity', visible: true, order: 8 },
];

const STORAGE_KEY = 'nexus:dashboard-layout';

function loadLayout(): DashboardWidget[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DashboardWidget[];
      // Merge with defaults to pick up any newly added widgets
      const ids = new Set(parsed.map((w) => w.id));
      const merged = [...parsed];
      for (const def of DEFAULT_WIDGETS) {
        if (!ids.has(def.id)) {
          merged.push({ ...def, order: merged.length });
        }
      }
      return merged;
    }
  } catch {
    // corrupt storage — fall through to defaults
  }
  return DEFAULT_WIDGETS;
}

function persist(widgets: DashboardWidget[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
}

interface DashboardLayoutState {
  widgets: DashboardWidget[];
  editing: boolean;
  toggleEditing: () => void;
  toggleWidget: (id: string) => void;
  moveWidget: (id: string, direction: 'up' | 'down') => void;
  reset: () => void;
}

export const useDashboardLayoutStore = create<DashboardLayoutState>((set) => ({
  widgets: loadLayout(),
  editing: false,

  toggleEditing: () => set((s) => ({ editing: !s.editing })),

  toggleWidget: (id) => {
    set((s) => {
      const widgets = s.widgets.map((w) =>
        w.id === id ? { ...w, visible: !w.visible } : w,
      );
      persist(widgets);
      return { widgets };
    });
  },

  moveWidget: (id, direction) => {
    set((s) => {
      const widgets = [...s.widgets].sort((a, b) => a.order - b.order);
      const idx = widgets.findIndex((w) => w.id === id);
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= widgets.length) return s;
      // Swap orders
      const temp = widgets[idx].order;
      widgets[idx] = { ...widgets[idx], order: widgets[swapIdx].order };
      widgets[swapIdx] = { ...widgets[swapIdx], order: temp };
      persist(widgets);
      return { widgets };
    });
  },

  reset: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({ widgets: DEFAULT_WIDGETS });
  },
}));
